import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink } from 'react-router-dom';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from '@material-ui/icons/Person';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import './NavbarTextil.scss';
import LogoContainer from 'containers/LogoContainer';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Button from 'components/commons/Button';
import { TextField } from '@material-ui/core';
import Menu from 'components/main/Header/Menu';
import { useLayoutEffect } from 'react';

const NavBarTextil = (
  { cartSize, categories, closeMenu, isMenuActive, isUserLoggedIn, logout, openLoginModal,
    openMenu, openSearchModal, user, show, client, subCategories, openSearch, closeSearch, 
    isSearchActive, products, filterOptions, setSelectedProduct,
    selectedProduct, onSearchProductChange, searchProduct, onSubmit, renderItem, hasFrequentQuestions = false,}
  ) => {
  const sortedSubs = (subCategories ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));

  const [open, setOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const handleScroll = () => {
    setScrollY(window.scrollY);
  };
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [openCatIds, setOpenCatIds] = useState({});
  const toggleCat = (id) => {
    setOpenCatIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const [catsOpen, setCatsOpen] = useState(false);
  const megaCloseTimer = React.useRef(null);

  const openMega = () => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
    setIsMegaOpen(true);
  };
  const closeMega = (delay = 120) => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
    megaCloseTimer.current = setTimeout(() => setIsMegaOpen(false), delay);
  };
  const onMegaKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsMegaOpen(false);
      const trigger = e.currentTarget.querySelector('.mega-dropdown-trigger');
      // dar tiempo a que se cierre el panel antes de enfocar
      setTimeout(() => { if (trigger) trigger.focus(); }, 0);
    }
  };

  const catCodeById = useMemo(() => {
    const map = {};
    (categories ?? []).forEach(c => { map[c.id] = c.code; });
    return map;
  }, [categories]);
  
  useEffect(() => {
    // Agregar un event listener cuando el componente se monta
    window.addEventListener('scroll', handleScroll);

    // Limpiar el event listener cuando el componente se desmonta
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const renderResponsiveMenu = () => (
    <>
      <div
        className={`fs-menu-overlay${isMenuActive ? ' pointer-active' : ''}`}
        role="presentation"
        onClick={closeMenu}
      />
      <div className={`hamburger-menu${isMenuActive ? ' active' : ''}`} style={{ top: 100 - scrollY }}>
        <div className="hamburger-menu-content">
          <Menu
            categories={categories}
            closeMenu={closeMenu}
            isMobile
            isUserLoggedIn={isUserLoggedIn}
            ulClassName="menu-top-nav"
            client={client}
            hasFrequentQuestions={hasFrequentQuestions}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="textil-nav">
      <nav className="textil-navbar">
        <div className="textil-inner">
          {/* hamburguesa */}
          <button
            className="textil-hamburger"
            aria-label="Abrir menú"
            aria-expanded={open}
            aria-controls="textil-drawer"
            onClick={() => setOpen(v => !v)}
          >
            {open ? <CloseIcon fontSize="large" /> : <MenuIcon fontSize="large" />}
          </button>

          {/* logo */}
          <Link to="/" className="textil-logo" aria-label="Inicio">
            <LogoContainer />
          </Link>

          {/* links desktop */}
          <ul className="textil-links">
            <li><Link to="/home">Inicio</Link></li>
            <li><Link to="/productos">Productos</Link></li>
            <li><Link to="/ofertas">Ofertas</Link></li>
            <li className="products-menu-link">
              <div
                className="mega-dropdown-container"
                onMouseEnter={openMega}
                onMouseLeave={() => closeMega()}
                onFocus={openMega}
                onBlur={() => closeMega()}
                onKeyDown={onMegaKeyDown}
              >
                <NavLink
                  to="/productos"
                  className="mega-dropdown-trigger"
                  activeClassName="active"
                  onClick={closeMenu}
                  aria-haspopup="true"
                  aria-expanded={isMegaOpen}
                  aria-controls="mega-menu-panel"
                >
                  Categorías
                </NavLink>
                <div id="mega-menu-panel" className={`mega-menu ${isMegaOpen ? 'open' : ''}`} role="menu">
                  <div className="mega-menu-content" tabIndex={-1}>
                    {categories.map((category) => {
                      const subs = subCategories.filter(
                        (sub) => sub.category_id === category.id
                      );
                      return (
                        <div key={category.id} className="mega-menu-column">
                          <NavLink
                            to={`/productos/${category.code}`}
                            className="mega-menu-category-title"
                            onClick={closeMenu}
                            role="menuitem"
                          >
                            {category.name}
                          </NavLink>

                          {subs.length > 0 && (
                            <ul className="mega-menu-sublist">
                              {subs.map((subcat) => (
                                <li key={subcat.id}>
                                  <NavLink
                                    to={`/productos/${category.code}/${subcat.code}`}
                                    className="mega-menu-subitem"
                                    onClick={closeMenu}
                                    role="menuitem"
                                  >
                                    {subcat.name}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </li>
          </ul>

          {/* buscador desktop */}
          <div className="textil-searchWrap">
            <div className="autocomplete">
              <Autocomplete
                key="auto-complete-search"
                id="combo-box-demo"
                options={products ?? []}
                freeSolo
                filterOptions={(options) => options}
                onChange={(event, product) => setSelectedProduct(event, product)}
                value={selectedProduct ?? null}
                onInputChange={onSearchProductChange}
                inputValue={searchProduct}
                getOptionSelected={(option, product) => option?.id === product?.id}
                getOptionLabel={(option) => typeof option === 'string' ? option : (option?.name ?? '')}
                renderOption={(option) => (
                  <div className="search-item" data-testid={`search-item-${option?.id}`}>
                    <div className="search-item__img">
                      <img src={option?.image1} alt={option?.name} loading="lazy" />
                    </div>
                    <div className="search-item__info">
                      <div className="search-item__title" title={option?.name || ''}>
                        {option?.name || ''}
                      </div>
                      <div className="search-item__price">
                        {option?.sale
                          ? <>
                              <span className="search-item__price--sale">
                                ${option.sale.toLocaleString('de-DE')}
                              </span>
                              <span className="search-item__price--old">
                                ${option.price.toLocaleString('de-DE')}
                              </span>
                            </>
                          : <span>${(option?.price ?? 0).toLocaleString('de-DE')}</span>
                        }
                      </div>
                    </div>
                  </div>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Buscar..." />
                )}
              />
              {searchProduct.length >= 3 && (
                <Button
                  buttonClass="nabvar-btn-search"
                  onClick={onSubmit}
                  text={<i className="fa fa-search icon-black" aria-hidden="true" />}
                  type="button"
                />
              )}
            </div>
          </div>

          {/* acciones derecha */}
          <div className="textil-actions" aria-label="acciones">
            {!user ? (
              <button className="navbar-icon btn-reset" onClick={openLoginModal} aria-label="Ingresar">
                <i className="fa fa-sign-in" aria-hidden="true" />
              </button>
            ) : (
              <div className="navbar-icon">
                <div className="dropdown">
                  <i className="fa fa-user" aria-hidden="true" />
                  <div className="dropdown-content">
                    <NavLink className="top-nav-item top-nav-border-right" to="/perfil">
                      <i className="fa fa-user" aria-hidden="true" /> Mi cuenta
                    </NavLink>
                    <NavLink to="/compras" className="top-nav-item top-nav-border-right" data-testid="nav-purchases">
                      Mis compras
                    </NavLink>
                    <a href={() => false} onClick={logout}>Cerrar sesión</a>
                  </div>
                </div>
              </div>
            )}
            <Link to="/carrito" className="textil-action textil-cart" aria-label="Carrito">
              <ShoppingCartIcon />
              {cartSize > 0 && <span className="textil-badge">{cartSize}</span>}
            </Link>
          </div>

          {/* acciones compactas solo mobile (carrito/login visibles) */}
          <div className="textil-actions--mobile">
            <Link to="/carrito" className="textil-action textil-cart" aria-label="Carrito">
              <ShoppingCartIcon />
              {cartSize > 0 && <span className="textil-badge">{cartSize}</span>}
            </Link>
            {!user && (
              <button className="navbar-icon btn-reset" onClick={openLoginModal} aria-label="Ingresar">
                <i className="fa fa-sign-in" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </nav>

      {open && <div className="textil-overlay" onClick={() => setOpen(false)} />}

      <div id="textil-drawer" className={`textil-drawer${open ? ' open' : ''}`} role="dialog" aria-modal="true">
        <div className="textil-drawer-search">
          <div className="autocomplete">
            <Autocomplete
              key="auto-complete-search-mobile"
              id="combo-box-mobile"
              options={products ?? []}
              freeSolo
              filterOptions={(options) => options}
              onChange={(event, product) => { setSelectedProduct(event, product); setOpen(false); }}
              value={selectedProduct ?? null}
              onInputChange={onSearchProductChange}
              inputValue={searchProduct}
              getOptionSelected={(option, product) => option?.id === product?.id}
              getOptionLabel={(option) => typeof option === 'string' ? option : (option?.name ?? '')}
              renderInput={(params) => (
                <TextField {...params} placeholder="Buscar..." />
              )}
            />
            {searchProduct.length >= 3 && (
              <Button
                buttonClass="nabvar-btn-search"
                onClick={() => { onSubmit(); setOpen(false); }}
                text={<i className="fa fa-search icon-black" aria-hidden="true" />}
                type="button"
              />
            )}
          </div>
        </div>

        <ul className="textil-drawer-list">
          <li>
            <Link className="textil-drawer-link" to="/productos" onClick={() => setOpen(false)}>Productos</Link>
          </li>
          <li>
            <Link className="textil-drawer-link" to="/ofertas" onClick={() => setOpen(false)}>Ofertas</Link>
          </li>
          {/* Acordeón Categorías → Subcategorías */}
          <li className="textil-accordion">
            <div className="textil-accordion__divider">
              <button
                className="textil-accordion__toggle"
                type="button"
                onClick={() => setCatsOpen(v => !v)}
                aria-expanded={catsOpen}
                aria-controls="drawer-subs"
              >
                <span className="textil-accordion__label">Subcategorías</span>
              </button>
              <span aria-hidden className={`textil-accordion__chev ${catsOpen ? 'open' : ''}`}>▾</span>
            </div>

            {/* Lista plana: solo subcategorías, ordenadas */}
            <ul id="drawer-subs" className={`textil-accordion__panel drawer-sublist ${catsOpen ? 'open' : ''}`}>
              {sortedSubs.map((sub) => {
                 const catCode = catCodeById[sub.category_id];
                 if (!catCode) return null;
                 return(
                  <li key={sub.id} className="textil-accordion__item">
                    <Link
                      to={`/productos/${catCode}/${sub.code}`} 
                      onClick={() => setOpen(false)}
                      className="textil-accordion__catLink"
                    >
                      {sub.name}
                    </Link>
                  </li>
                 )
                 })}
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default NavBarTextil;
